import type { ComponentProps, ReactNode } from 'react';

import type { ColorName, ColorVariant, Style, TooltipPosition } from './buttonClasses';
import type { Size } from './types';
import { cn } from '../cn';
import { buttonClassName, composeVariant, softIconColorClasses } from './buttonClasses';
import Tooltip from './Tooltip';
import { sizeHeight } from './types';

export type IconButtonProps = Omit<ComponentProps<'button'>, 'size'> & {
  size?: Size;
  /** Color axis — `'blue' | 'red' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
  tone?: Style;
  active?: boolean;
  tooltip?: ReactNode;
  tooltipMaxWidth?: number;
  tooltipPosition?: TooltipPosition;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  bordered?: boolean;
  icon: ReactNode;
};

export default function IconButton({
  size = 'md',
  color,
  tone,
  active = false,
  bordered = false,
  error = false,
  disabled = false,
  readOnly = false,
  className,
  icon,
  tooltip,
  tooltipMaxWidth,
  tooltipPosition,
  onClick,
}: IconButtonProps) {
  const heightClass = sizeHeight(size);
  const widthClass = heightClass.replace('h-', 'w-');
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');

  return (
    <Tooltip
      as={readOnly ? 'span' : 'button'}
      tooltip={tooltip}
      tooltipMaxWidth={tooltipMaxWidth}
      tooltipPosition={tooltipPosition}
      variant={effectiveVariant}
      onClick={onClick}
      {...(!readOnly && { disabled, type: 'button' })}
      className={cn(
        buttonClassName({
          size,
          bordered,
          icon: true,
          children: false,
          error,
          variant: effectiveVariant,
          active,
          text: false,
          filled: false,
          disabled,
          readOnly,
          tooltip: !!tooltip,
          className,
        }),
        heightClass,
        widthClass,
      )}>
      <span
        className={cn('w-full h-full flex items-center justify-center *:size-full', softIconColorClasses(effectiveVariant, active))}>
        {icon}
      </span>
    </Tooltip>
  );
}
